import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiErrorResponse } from "../models/ApiResponse";
import {
	BucketSetting,
	ComparisonMode,
	ExplainWidgetResponse,
	FilterOption,
	GenerateWidgetRequest,
	GenerateWidgetResponse,
	LakeQueryRequest,
	LakeQueryResponse,
	PublicDashboard,
	ReportFilters,
	ReportPlan,
	ReportShareInfo,
	SharedDashboard,
	WidgetSpec,
} from "../models/ReportBuilder";
import { apiStore } from "../stores/api.store";

/** Public viewers refetch this often; a TV should update itself. */
export const PUBLIC_REFRESH_MS = 5 * 60 * 1000;

/** Prompt → widget spec (+ preview) with one model call on the backend. */
export function useGenerateWidget() {
	return useMutation<GenerateWidgetResponse, ApiErrorResponse, GenerateWidgetRequest>((body) =>
		apiStore
			.post<GenerateWidgetResponse>({ path: "/ai/widget/generate", body, isAuthRequired: true })
			.then((d) => d.data)
	);
}

export function useCreateShare() {
	return useMutation<
		ReportShareInfo,
		ApiErrorResponse,
		{ dashboard: SharedDashboard; pin?: string }
	>((body) =>
		apiStore
			.post<ReportShareInfo>({ path: "/ai/report/share", body, isAuthRequired: true })
			.then((d) => d.data)
	);
}

export function useUpdateShare() {
	return useMutation<
		ReportShareInfo,
		ApiErrorResponse,
		{ shareId: string; dashboard?: SharedDashboard; pin?: string }
	>(({ shareId, ...body }) =>
		apiStore
			.put<ReportShareInfo>({ path: `/ai/report/share/${shareId}`, body, isAuthRequired: true })
			.then((d) => d.data)
	);
}

export function useDeleteShare() {
	return useMutation<unknown, ApiErrorResponse, { shareId: string }>(({ shareId }) =>
		apiStore.delete({ path: `/ai/report/share/${shareId}`, isAuthRequired: true })
	);
}

/** Public: the shared report's definition, refreshed every five minutes. */
export function usePublicReport(shareId: string | undefined, pin?: string) {
	return useQuery<PublicDashboard, ApiErrorResponse>(
		["public-report", shareId, pin ?? ""],
		() =>
			apiStore
				.get<PublicDashboard>({
					path: `/public/report/${shareId}`,
					query: pin ? { pin } : undefined,
					isAuthRequired: false,
				})
				.then((d) => d.data),
		{
			enabled: !!shareId,
			retry: false,
			refetchInterval: PUBLIC_REFRESH_MS,
			refetchIntervalInBackground: true,
			staleTime: PUBLIC_REFRESH_MS,
		}
	);
}

/** Public: one widget's data, refreshed every five minutes. Filters are the share's, bound server-side. */
export function usePublicWidgetData(
	shareId: string,
	widgetId: string,
	range: { from: string; to: string },
	compare?: ComparisonMode,
	pin?: string,
	bucket?: BucketSetting,
	enabled = true
) {
	return useQuery<LakeQueryResponse, ApiErrorResponse>(
		[
			"public-widget",
			shareId,
			widgetId,
			range.from,
			range.to,
			compare ?? "none",
			bucket ?? "auto",
			pin ?? "",
		],
		() =>
			apiStore
				.post<LakeQueryResponse>({
					path: `/public/report/${shareId}/query`,
					body: { widgetId, from: range.from, to: range.to, compare, bucket, pin },
					isAuthRequired: false,
				})
				.then((d) => d.data),
		{
			enabled,
			retry: false,
			refetchInterval: PUBLIC_REFRESH_MS,
			refetchIntervalInBackground: true,
			staleTime: PUBLIC_REFRESH_MS,
			keepPreviousData: true,
		}
	);
}

const filtersKey = (filters?: ReportFilters) => JSON.stringify(filters ?? {});

/**
 * Runs a widget's SQL for a date range, grain and filters. Cached per
 * request so a report reloads only when one of those changes; an optional
 * interval keeps the internal view fresh (TV mode).
 */
export function useWidgetData(
	request: LakeQueryRequest | undefined,
	options: { refetchIntervalMs?: number } = {}
) {
	return useQuery<LakeQueryResponse, ApiErrorResponse>(
		[
			"lake-query",
			request?.sql,
			request?.from,
			request?.to,
			request?.compare ?? "none",
			request?.bucket ?? "auto",
			filtersKey(request?.filters),
		],
		() =>
			apiStore
				.post<LakeQueryResponse>({ path: "/ai/lake/query", body: request, isAuthRequired: true })
				.then((d) => d.data),
		{
			enabled: !!request?.sql,
			staleTime: 5 * 60 * 1000,
			retry: false,
			keepPreviousData: true,
			refetchInterval: options.refetchIntervalMs,
			refetchIntervalInBackground: !!options.refetchIntervalMs,
		}
	);
}

/** Plain-Swedish explanation of a widget from the backend. */
export function useExplainWidget() {
	return useMutation<ExplainWidgetResponse, ApiErrorResponse, { spec: WidgetSpec }>((body) =>
		apiStore
			.post<ExplainWidgetResponse>({ path: "/ai/widget/explain", body, isAuthRequired: true })
			.then((d) => d.data)
	);
}

/** "Bygg en rapport om …" → name, period and widget prompts. */
export function usePlanReport() {
	return useMutation<ReportPlan, ApiErrorResponse, { prompt: string }>((body) =>
		apiStore
			.post<ReportPlan>({ path: "/ai/report/plan", body, isAuthRequired: true })
			.then((d) => d.data)
	);
}

/** Column names per lake-backed dimension; the option lists are plain queries on the org's own database. Koncern/dotterbolag options come from the REST lists instead. */
const OPTION_SOURCE: Partial<
	Record<keyof ReportFilters, { from: string; id: string; name: string; where?: string }>
> = {
	customerIds: { from: "customers", id: "id", name: "name" },
	poolIds: {
		from: "consultants",
		id: "pool_id",
		name: "min(pool_name)",
		where: "pool_id is not null",
	},
	consultantIds: { from: "consultants", id: "id", name: "full_name" },
	contractIds: { from: "contracts", id: "id", name: "name" },
};

const sqlLiteral = (value: string) => `'${value.replace(/'/g, "''")}'`;
/** For ilike: quotes doubled and the wildcard characters escaped (backslash is Postgres' default escape). */
const sqlLike = (value: string) => sqlLiteral(value.replace(/[%_\\]/g, (c) => `\\${c}`));

function optionSql(dimension: keyof ReportFilters, search: string, ids?: string[]): string {
	const src = OPTION_SOURCE[dimension];
	if (!src) throw new Error(`${dimension} has no lake option source`);
	const grouped = src.name.includes("(");
	const nameColumn = grouped ? src.name.replace(/^min\((.*)\)$/, "$1") : src.name;
	const conditions = [src.where, `${src.id} is not null`];
	if (search.trim()) conditions.push(`${nameColumn} ilike ${sqlLike(`%${search.trim()}%`)}`);
	if (ids?.length) conditions.push(`${src.id} in (${ids.map(sqlLiteral).join(", ")})`);
	const where = conditions.filter(Boolean).join(" and ");
	const groupBy = grouped ? ` group by ${src.id}` : "";
	return `select ${src.id} as id, ${src.name} as name from ${src.from} where ${where}${groupBy} order by 2 limit 200`;
}

const toOptions = (d: { data: LakeQueryResponse }): FilterOption[] =>
	d.data.rows.map((r) => ({ id: String(r.id), name: String(r.name ?? r.id) })).filter((o) => o.id);

/** Options for one filter dimension matching a search, read from the lake itself so names match the widgets. */
export function useFilterOptions(dimension: keyof ReportFilters, search: string, enabled = true) {
	const lakeBacked = !!OPTION_SOURCE[dimension];
	return useQuery<FilterOption[], ApiErrorResponse>(
		["lake-filter-options", dimension, search.trim().toLowerCase()],
		() =>
			apiStore
				.post<LakeQueryResponse>({
					path: "/ai/lake/query",
					body: { sql: optionSql(dimension, search), maxRows: 200 },
					isAuthRequired: true,
				})
				.then(toOptions),
		{
			enabled: enabled && lakeBacked,
			staleTime: 30 * 60 * 1000,
			retry: false,
			keepPreviousData: true,
		}
	);
}

/** Names of already selected ids, for the chips. */
export function useFilterNames(dimension: keyof ReportFilters, ids: string[]) {
	const lakeBacked = !!OPTION_SOURCE[dimension];
	return useQuery<FilterOption[], ApiErrorResponse>(
		["lake-filter-names", dimension, [...ids].sort().join(",")],
		() =>
			apiStore
				.post<LakeQueryResponse>({
					path: "/ai/lake/query",
					body: { sql: optionSql(dimension, "", ids), maxRows: 200 },
					isAuthRequired: true,
				})
				.then(toOptions),
		{
			enabled: ids.length > 0 && lakeBacked,
			staleTime: 30 * 60 * 1000,
			retry: false,
			keepPreviousData: true,
		}
	);
}
